import { Boxes, CalendarPlus, Columns3, FileText, LifeBuoy, Repeat, Search, UserPlus } from "lucide-react";
import { CREATE_EVENT_FRESH_START_PARAM } from "@/features/create-event-v3/_logic/constants";
import { SUPPORT_LABEL } from "@/features/support-chat/_logic/constants";
import { INVITE_PARAM } from "@/features/users-groups/_logic/constants";
import { DISABLED_REASON } from "./constants";
import { KEYWORDS_BY_ID } from "./keywords";
import { PAGE_IDS } from "./pages";
import type { PaletteItem } from "./types";

/**
 * The handful of things a planner comes here to *do*, as opposed to somewhere
 * they want to go.
 *
 * Kept deliberately short. Quick actions earn their place at the top of the
 * palette by being few enough to hit without reading, which is also why they
 * carry Cmd+1..Cmd+N accelerators. Adding a sixth would make the row a list.
 *
 * Every one of these is reachable elsewhere in the product. That duplication is
 * the point: the whole project exists because "reachable" and "found" are not
 * the same thing.
 *
 * @returns The quick actions, in accelerator order.
 */
export function buildActionItems(): PaletteItem[] {
  return [
    {
      id: "action-new-event",
      label: "New event",
      group: "actions",
      icon: CalendarPlus,
      // The most-used route outside the dashboard: 2,304 people in 90 days, so
      // it keeps its own row rather than hiding one level down inside Create.
      keywords: ["create event", "start", "new offsite", "plan"],
      scope: "global",
      // `?fresh=1` for the same reason the Events page's own Create button
      // sends it (`EventsPageContent.tsx`): without it, a signed-in user with an
      // open draft is silently dropped back into that half-finished draft, so
      // the one command that says "New event" is the one that does not start one.
      run: { kind: "navigate", href: `/create?${CREATE_EVENT_FRESH_START_PARAM}=1` },
      // Never from the wizard itself. `?fresh=1` is a same-route push, so it is
      // either destructive or inert depending on the URL you are already on:
      // from `/create` it flips `wizardKey` and remounts the wizard blank with
      // no confirmation, and the blank wizard's first autosave then INSERTS a
      // second draft row (`saveDraftField(null, ...)` creates rather than
      // updates). `getLatestOpenDraft` takes the newest row, so the older,
      // fuller draft stops being offered on resume. From `/create?fresh=1`,
      // which is where this very row lands you, the key never changes and the
      // command does nothing at all.
      requires: (context) => context.surface !== "create",
      disabledReason: DISABLED_REASON.alreadyHere,
    },
    {
      id: "action-request-venues",
      label: "Request venues",
      group: "actions",
      icon: Search,
      // The event home page's own primary CTA at the first stage, so the palette
      // offers the same verb the product already leads with.
      keywords: [...(KEYWORDS_BY_ID.search ?? []), "rfp", "request", "send rfp"],
      scope: "event",
      run: { kind: "navigate", href: "/dashboard/source/{eventId}/search/hotel" },
    },
    {
      id: "action-view-proposals",
      label: "View proposals",
      group: "actions",
      icon: FileText,
      // Second-most-reached surface in the product (215 of 569 client accounts in
      // 90 days), and "did anything come back yet?" is the most repeated check on
      // the spine — ENG-490 exists because clients kept asking exactly that.
      keywords: [...(KEYWORDS_BY_ID.proposals ?? []), "did they respond", "any quotes yet", "responses"],
      scope: "event",
      run: { kind: "navigate", href: "/dashboard/source/{eventId}/proposals" },
    },
    {
      id: "action-compare",
      label: "Compare proposals...",
      group: "actions",
      icon: Columns3,
      keywords: KEYWORDS_BY_ID.compare,
      scope: "event",
      // Pushes a page rather than navigating. The compare route calls notFound()
      // without `?venues=`, so the selection has to be made before we leave.
      run: { kind: "page", page: PAGE_IDS.compare },
    },
    {
      id: "action-invite",
      label: "Invite a teammate",
      group: "actions",
      icon: UserPlus,
      keywords: KEYWORDS_BY_ID.users,
      scope: "org",
      // Opens the invite modal on arrival rather than dropping the user on the
      // users table to find the button. Navigate and pre-fill, never send.
      run: { kind: "navigate", href: `/dashboard/organization/users/users?${INVITE_PARAM}=1` },
    },
  ];
}

/**
 * Actions that belong in the palette but not on the accelerator row.
 *
 * Each lands in the group a user would look in rather than piling into
 * `actions`: the quick row earns its place by staying short, and a ninth row
 * with no accelerator is not a quick action, it is a list.
 *
 * Each one exists in the product and is reachable only from somewhere people do
 * not look: three of these live under the avatar menu at the bottom of the
 * sidebar. Surfacing them is the cheapest version of this project's thesis.
 *
 * "Change event details" is deliberately absent. It pointed at
 * `/{eventId}/change-event-details`, which `app/[event_id]/layout.tsx` lists in
 * `ADMIN_ONLY_SEGMENTS`: a non-admin is redirected to `/dashboard` and lands on
 * the org Events list, having asked to edit an event and been given a list of
 * events. The client-facing edit surface is the dashboard Details page, which
 * is where the Events card's own "Edit details" goes and what the "Event
 * details" row already opens, so `edit` and `rename` live on that row instead.
 *
 * @returns The secondary actions.
 */
export function buildSecondaryActions(): PaletteItem[] {
  return [
    {
      id: "action-switch-event",
      label: "Switch event...",
      group: "events",
      icon: Repeat,
      keywords: ["change event", "another event", "other event", "swap"],
      scope: "global",
      run: { kind: "page", page: PAGE_IDS.pickEvent },
    },
    {
      id: "action-switch-org",
      label: "Switch organization",
      group: "org",
      icon: Boxes,
      // Buried under the avatar menu. It also makes a specific failure
      // recoverable: the palette's event list is scoped to the active org, so
      // "my event isn't in here" is usually "wrong org".
      keywords: ["change organization", "org", "workspace", "tenant", "company"],
      scope: "global",
      run: { kind: "action", fn: (context) => context.changeOrganization() },
      // Clearing the active org only means something where the picker lives.
      // The gate is in `dashboard/layout.tsx`, so off the dashboard this cleared
      // the cookie, re-rendered the same page, showed no picker, and silently
      // widened this palette's own event list to every org the user belongs to.
      requires: (context) => context.surface === "dashboard",
      disabledReason: DISABLED_REASON.dashboardOnly,
    },
    {
      id: "action-support",
      // The sidebar's own label, read from the same constant rather than
      // retyped: one button should not have two names, and "Get help" is the
      // one the product already shows and the one people searched for.
      label: SUPPORT_LABEL,
      group: "help",
      icon: LifeBuoy,
      keywords: ["help", "support", "contact support", "chat", "ask someone", "problem", "stuck"],
      scope: "global",
      run: { kind: "action", fn: (context) => context.openSupportChat() },
      // The palette outlives the enterprise shell now: on `/create` there is no
      // `SupportChatProvider` to open (ENG-835). Disabled with a reason rather
      // than hidden, the same call `comingSoon` makes in the registry, because a
      // row that vanishes on one route reads as the feature being gone.
      //
      // Keyed on the provider actually being there rather than on `surface`, so
      // a shell that loses the provider disables the row instead of throwing.
      // Surface-neutral wording on purpose: the gate reads provider presence,
      // not surface, so "Dashboard only" would be a lie in exactly the case the
      // comment above anticipates, a shell that lost its provider.
      requires: (context) => context.canOpenSupportChat,
      disabledReason: DISABLED_REASON.unavailableHere,
    },
  ];
}
